import { LIVE_UPDATES_PATH } from 'constants/liveUpdates'
import useFlag from 'hooks/useFlag'
import { useGiftCardsAccess } from 'hooks/useGiftCardsAccess'
import { useLiveUpdatesAccess } from 'hooks/useLiveUpdatesAccess'
import { useOrderingAccess } from 'hooks/useOrderingAccess'
import {
  formatPermissionReason,
  usePermissionLabel,
  usePermissionReason,
  useRbacNavDisabled,
  useRbacNavDisabledWithReason,
} from 'hooks/useRbacNavCheck'
import { useReportPermission } from 'hooks/useReportPermission'
import { buildTranslate } from 'locales'
import React from 'react'
import {
  DIGITAL_ORDERING_FLAG,
  DIGITAL_ORDERING_PERMISSION,
} from 'scenes/Reports/DigitalOrderingReport/gate'
import { useAbility } from 'utilities/ability'
import { type CaslSubject } from 'utilities/permissionMap'
import NavLink from '../Bar/NavLink'

const t = buildTranslate('nav.experiences_menu')

type PropsT = {
  onClick?: () => void
  isLeftNav?: boolean
}

const ExperiencesContent: React.FC<PropsT> = ({
  isLeftNav = false,
  onClick = () => {},
}) => {
  const ability = useAbility()
  const operationsCenterEnabled = useFlag('operations-center', false)
  // Brand & Content requires ANY of CMS, AppConfiguration, or DigitalExperiences.
  // The backend has split content_management into sub-resources; allow access
  // if the user holds at least one of them.
  const cmsDisabled = useRbacNavDisabled('CMS')
  const appConfigDisabled = useRbacNavDisabled('AppConfiguration')
  const digitalExpDisabled = useRbacNavDisabled('DigitalExperiences')
  const brandContentDisabled =
    cmsDisabled && appConfigDisabled && digitalExpDisabled

  const cmsLabel = usePermissionLabel('CMS')
  const appConfigLabel = usePermissionLabel('AppConfiguration')
  const digitalExpLabel = usePermissionLabel('DigitalExperiences')
  const brandContentReason = (() => {
    if (!brandContentDisabled) return undefined
    const labels = [cmsLabel, appConfigLabel, digitalExpLabel].filter(Boolean)
    if (labels.length === 0) return undefined
    return `Requires one of "${labels.join('", "')}" permissions`
  })()

  const showOrderingManagement = useFlag('mx-4-ordering-management', false)
  const ansaReportsEnabled =
    useFlag('display-ansa-reports', false) &&
    ability.can('access', 'AnsaReports')
  const { orderingEnabled, supportsOrderingManagement } = useOrderingAccess()
  const orderingManagementEnabled =
    orderingEnabled && supportsOrderingManagement

  const isReuseableSmartLinksEnabled = useFlag('reuseable-smart-links', false)
  const smartLinks = useRbacNavDisabledWithReason('SmartLinks')

  // Flag + RBAC gate composed in one shared hook so the nav and the scene
  // can't drift on "can this merchant use Gift Cards".
  const giftCards = useGiftCardsAccess()

  // Flag + eligibility + RBAC gate composed in one shared hook so the nav and
  // the scene can't drift on "can this merchant use Live Updates" (see
  // useLiveUpdatesAccess).
  const liveUpdates = useLiveUpdatesAccess()

  const appDownloadsDisabled = ability.cannot('access', 'LocationReports')
  const appDownloadsReason = usePermissionReason(
    'LocationReports',
    appDownloadsDisabled
  )

  // Flag and permission for the "Digital ordering" nav entry. Both come from
  // the report's own gate module, which the page reads too — the nav link and
  // the route it points at therefore cannot disagree. See that module for why
  // this is shared rather than mirrored by hand.
  const digitalOrderingReportsEnabled = useFlag(DIGITAL_ORDERING_FLAG, false)

  const digitalOrderingPermission = useReportPermission(
    DIGITAL_ORDERING_PERMISSION
  )
  const digitalOrderingDisabled = ability.cannot(
    digitalOrderingPermission.action,
    digitalOrderingPermission.resource
  )
  // Narrow the resolved resource back to a CaslSubject literal so the tooltip
  // names the permission actually being checked above, in both regimes
  // (useReportPermission widens its return to `string`).
  const digitalOrderingSubject: CaslSubject =
    digitalOrderingPermission.resource === 'Reports'
      ? 'Reports'
      : 'LocationReports'
  const digitalOrderingReason = usePermissionReason(
    digitalOrderingSubject,
    digitalOrderingDisabled
  )

  const orderingDisabled = !orderingEnabled
  const orderingLabel = usePermissionLabel('OrderingManagement')
  const orderingReason = formatPermissionReason(
    orderingLabel,
    orderingDisabled,
    t('ordering_suffix')
  )
  const orderingManagementDisabled = !orderingManagementEnabled
  const orderingManagementReason = formatPermissionReason(
    orderingLabel,
    orderingManagementDisabled,
    t('ordering_management_suffix')
  )

  return (
    <>
      <NavLink
        isLeftNav={isLeftNav}
        onClick={onClick}
        text={t('b_and_c')}
        link="/brand_content"
        disabled={brandContentDisabled}
        disabledReason={brandContentReason}
      />
      {liveUpdates.show && (
        <NavLink
          isLeftNav={isLeftNav}
          onClick={onClick}
          text={t('live_updates')}
          link={LIVE_UPDATES_PATH}
          status="new"
          disabled={liveUpdates.disabled}
          disabledReason={liveUpdates.disabledReason}
        />
      )}
      <NavLink
        isLeftNav={isLeftNav}
        onClick={onClick}
        text={t('app_downloads')}
        link="/reports/app_downloads"
        disabled={appDownloadsDisabled}
        disabledReason={appDownloadsReason}
      />
      {digitalOrderingReportsEnabled && (
        <NavLink
          isLeftNav={isLeftNav}
          onClick={onClick}
          text={t('digital_ordering')}
          link="/reports/digital_ordering"
          disabled={digitalOrderingDisabled}
          disabledReason={digitalOrderingReason}
        />
      )}
      {!operationsCenterEnabled && showOrderingManagement && (
        <NavLink
          isLeftNav={isLeftNav}
          onClick={onClick}
          text={t('ordering_management')}
          link="/ordering_management"
          disabled={orderingManagementDisabled}
          disabledReason={orderingManagementReason}
        />
      )}
      <NavLink
        isLeftNav={isLeftNav}
        onClick={onClick}
        text={t('ordering_conversion')}
        link="/reports/ordering_conversion"
        disabled={orderingDisabled}
        disabledReason={orderingReason}
      />
      {isReuseableSmartLinksEnabled && (
        <NavLink
          isLeftNav={isLeftNav}
          onClick={onClick}
          text={t('smart_links')}
          link="/smart_links_overview"
          disabled={smartLinks.disabled}
          disabledReason={smartLinks.disabledReason}
        />
      )}
      {ansaReportsEnabled && (
        <NavLink
          isLeftNav={isLeftNav}
          onClick={onClick}
          text={t('stored_value')}
          link="/stored_value"
        />
      )}
      {giftCards.show && (
        <NavLink
          isLeftNav={isLeftNav}
          onClick={onClick}
          text={t('gift_cards')}
          link="/gift_cards"
          disabled={giftCards.disabled}
          disabledReason={giftCards.disabledReason}
        />
      )}
    </>
  )
}

export default ExperiencesContent
